import { t, useLanguage } from '../../i18n';
import React, { useState, useEffect, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { View, StyleSheet, ScrollView, TouchableOpacity, Alert, Image, DeviceEventEmitter } from 'react-native';
import { Text, Card, Avatar, Button, List, Switch, useTheme, Divider } from 'react-native-paper';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { signOut, updateProfile } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';

type ProfileScreenProps = {
  navigation: any;
};

const YEAR_OPTIONS = [
  { label: '1年', value: '1' },
  { label: '2年', value: '2' },
  { label: '3年', value: '3' },
  { label: '4年', value: '4' },
  { label: '院1年', value: '5' },
  { label: '院2年', value: '6' },
];

const FACULTY_OPTIONS = [
  { label: '言語文化学部', value: 'lc' },
  { label: '国際社会学部', value: 'ia' },
  { label: '国際日本学部', value: 'js' },
  { label: '大学院総合国際学研究科', value: 'pg' },
];

const YEAR_LABEL_MAP: Record<string, string> = Object.fromEntries(
  YEAR_OPTIONS.map((option) => [option.value, option.label])
);

const FACULTY_LABEL_MAP: Record<string, string> = Object.fromEntries(
  FACULTY_OPTIONS.map((option) => [option.value, option.label])
);

const ProfileScreen = ({ navigation }: ProfileScreenProps) => {
  useLanguage();
  const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);

  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);


  const theme = useTheme();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);

      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };



  const displayFaculty = userData?.faculty
    ? FACULTY_LABEL_MAP[String(userData.faculty)] || String(userData.faculty)
    : '-';

  const displayYear = userData?.year
    ? YEAR_LABEL_MAP[String(userData.year)] || `${userData.year}年`
    : '-';

  const handleSignOut = async () => {
    Alert.alert(
      t("ログアウト"),
      t("ログアウトしますか？"),
      [
        { text: t("キャンセル"), style: 'cancel' },
        {
          text: t("ログアウト"),
          onPress: async () => {
            try {
              DeviceEventEmitter.emit('auth_guest_change', false);
              await signOut(auth);
              // Navigation will be handled by the auth state listener in App.tsx
            } catch (error) {
              console.error('Error signing out:', error);
              Alert.alert(t("エラー"), t("ログアウトに失敗しました"));
            }
          }
        }
      ]
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.colors.primary }]}>{t("プロフィール")}</Text>
        <TouchableOpacity onPress={toggleTheme}>
          <Ionicons name={isDarkMode ? 'sunny' : 'moon'} size={24} color={theme.colors.primary} />
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Card style={styles.profileCard}>
          <Card.Content style={styles.profileContent}>
            <Avatar.Text
              size={80}
              label={auth.currentUser?.displayName?.split(' ').map((n: string) => n[0]).join('') || 'U'}
              style={{ backgroundColor: theme.colors.primary }}
            />
            <View style={styles.profileInfo}>
              <Text style={[styles.userName, { color: theme.colors.onSurface }]}>
                {auth.currentUser?.displayName || t("ユーザー")}
              </Text>
              <Text style={[styles.userEmail, { color: theme.colors.onSurfaceVariant }]}>
                {auth.currentUser?.email}
              </Text>
              <Text style={[styles.userDepartment, { color: theme.colors.onSurfaceVariant }]}>
                {userData?.faculty || userData?.year ? `${t(displayYear)} / ${t(displayFaculty)}` : t("学部/学年未設定")}
              </Text>
            </View>
          </Card.Content>
          <Card.Actions style={styles.cardActions}>
            <Button
              mode="outlined"
              onPress={() => navigation.navigate('Settings')}
              icon="account-edit"
            >
              {t("プロフィール編集")}
            </Button>
          </Card.Actions>
        </Card>

        <Card style={styles.statsCard}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("履修状況")}</Text>
            <View style={styles.statsContainer}>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: theme.colors.primary }]}>12</Text>
                <Text style={styles.statLabel}>{t("履修中の授業")}</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: theme.colors.primary }]}>24</Text>
                <Text style={styles.statLabel}>{t("取得単位")}</Text>
              </View>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: theme.colors.primary }]}>3.8</Text>
                <Text style={styles.statLabel}>GPA</Text>
              </View>
            </View>
          </Card.Content>
        </Card>

        <Card style={styles.settingsCard}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("設定")}</Text>



            <List.Item
              title={t("テーマ")}
              description={isDarkMode ? t("ダークモード") : t("ライトモード")}
              left={props => <List.Icon {...props} icon={isDarkMode ? "moon" : "white-balance-sunny"} />}
              right={props => (
                <Switch
                  value={isDarkMode}
                  onValueChange={toggleTheme}
                  color={theme.colors.primary}
                />
              )}
            />

            <Divider />

            <List.Item
              title={t("アプリについて")}
              description={t("バージョン {0}", [Constants.expoConfig?.version || '1.0.0'])}
              left={props => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="information" size={24} color={props.color} />} />}
              onPress={() => {/* Show app info */ }}
            />

            <Divider />

            <List.Item
              title={t("ログアウト")}
              description={t("アカウントからログアウトします")}
              left={props => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="logout" size={24} color="#F44336" />} />}
              onPress={handleSignOut}
            />
          </Card.Content>
        </Card>

        <View style={styles.appInfo}>
          <Image
            source={require('../../assets/app_logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={[styles.appName, { color: theme.colors.primary }]}>{t("TUFS時間割")}</Text>
          <Text style={[styles.appVersion, { color: theme.colors.onSurfaceVariant }]}>Version {Constants.expoConfig?.version || '1.0.0'}</Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  scrollContent: {
    padding: 16,
  },
  profileCard: {
    marginBottom: 16,
  },
  profileContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  profileInfo: {
    marginLeft: 16,
    flex: 1,
  },
  userName: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    marginBottom: 4,
  },
  userDepartment: {
    fontSize: 14,
  },
  cardActions: {
    justifyContent: 'flex-end',
    paddingTop: 0,
  },
  statsCard: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 14,
    marginTop: 4,
  },
  settingsCard: {
    marginBottom: 24,
  },
  appInfo: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    width: 60,
    height: 60,
    marginBottom: 8,
  },
  appName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  appVersion: {
    fontSize: 14,
  },
});

export default ProfileScreen;
